import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import logoUrl from "../images/logo.svg";

import { GoogleSignInButton } from "../components/auth/GoogleSignInButton";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { authService } from "../services/auth";
import { flipmailAuthService } from "../services/flipmailAuth";
import { ONBOARDING_FIRST_PATH } from "../config/onboardingRoutes";
import { trackEvent } from "../utils/tracking";

const tabs = [
  { key: "signin", label: "Sign in" },
  { key: "register", label: "Create account" },
];

const Login = ({ authenticated }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formState, setFormState] = useState({
    email: "",
    password: "",
    fullName: "",
  });

  const isRegister = tab === "register";

  const canSubmit = useMemo(() => {
    if (!formState.email || !formState.password) return false;
    if (isRegister && !formState.fullName) return false;
    return true;
  }, [formState, isRegister]);

  React.useEffect(() => {
    if (!authenticated) return;
    authService.fetchUser(
      () => {
        const current = JSON.parse(localStorage.getItem("currentUser") || "{}");
        const isDone = current?.features?.onboarding?.completed;
        navigate(isDone ? "/audit/" : ONBOARDING_FIRST_PATH);
      },
      () => navigate("/audit/")
    );
  }, [authenticated, navigate]);

  const updateField = (name, value) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const completeAuthFlow = (isRegistration) => {
    authService.fetchUser(
      () => {
        const current = JSON.parse(localStorage.getItem("currentUser") || "{}");
        const isDone = current?.features?.onboarding?.completed;
        if (isRegistration) {
          navigate(isDone ? "/audit/" : ONBOARDING_FIRST_PATH);
        } else {
          navigate("/audit/");
        }
      },
      () => navigate(isRegistration ? ONBOARDING_FIRST_PATH : "/audit/")
    );
  };

  const formatRegisterError = (err) => {
    const data = err?.response?.data;
    if (!data) return "Unable to create account.";
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.email) && data.email[0]) return data.email[0];
    return "Unable to create account.";
  };

  const submitEmailFlow = async (event) => {
    event.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    setError("");
    if (isRegister) {
      authService.register(
        { email: formState.email, password: formState.password, full_name: formState.fullName },
        () => {
          trackEvent("register", { method: "email" });
          completeAuthFlow(true);
        },
        (err) => {
          trackEvent("auth_failed", { method: "email", flow: "register" });
          setError(formatRegisterError(err));
          setLoading(false);
        }
      );
      return;
    }
    authService.login(
      formState.email,
      formState.password,
      () => {
        trackEvent("login", { method: "email" });
        completeAuthFlow(false);
      },
      (err) => {
        trackEvent("auth_failed", { method: "email", flow: "signin" });
        setError(err?.response?.data?.detail || "Could not sign in.");
        setLoading(false);
      }
    );
  };

  const startGoogleAuth = () => {
    setLoading(true);
    setError("");
    trackEvent("oauth_start", { provider: "google", source: "login" });
    window.location.href = flipmailAuthService.getOAuthStartUrl(
      flipmailAuthService.OAUTH_PROVIDER_GOOGLE
    );
  };

  return (
    <div className="min-h-screen bg-theme-main flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <Link
            to="/"
            className="inline-flex flex-col items-center gap-2 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-theme-accent/60 focus-visible:ring-offset-2 focus-visible:ring-offset-theme-main"
            aria-label="Flipmail home"
          >
            <img src={logoUrl} alt="" width={44} height={44} className="h-11 w-11 shrink-0" />
            <span className="font-display font-bold italic text-[36px] leading-[1.1] text-theme-on-background transition-colors hover:text-theme-accent">
              Flipmail
            </span>
          </Link>
          <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-theme-dim mt-2">Confident Rebel Outbound</p>
        </div>

        <div className="bg-theme-surface border border-theme-border rounded-xl p-6">
          <div className="flex border-b border-theme-border mb-6">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`flex-1 pb-3 font-mono text-xs tracking-[0.06em] uppercase transition-colors ${
                  tab === item.key
                    ? "text-theme-on-background border-b border-theme-accent"
                    : "text-theme-muted hover:text-theme-text"
                }`}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <form className="space-y-4" onSubmit={submitEmailFlow}>
            {isRegister ? (
              <Input
                id="fullName"
                label="Full name"
                placeholder="Jane Doe"
                value={formState.fullName}
                onChange={(event) => updateField("fullName", event.target.value)}
              />
            ) : null}
            <Input
              id="email"
              type="email"
              label="Email"
              placeholder="you@company.com"
              value={formState.email}
              onChange={(event) => updateField("email", event.target.value)}
            />
            <Input
              id="password"
              type="password"
              label="Password"
              placeholder="••••••••"
              value={formState.password}
              onChange={(event) => updateField("password", event.target.value)}
            />

            {error ? <p className="font-mono text-xs text-rose-400">{error}</p> : null}

            <Button type="submit" disabled={!canSubmit || loading} className="w-full">
              {loading ? "Please wait..." : isRegister ? "Create account" : "Sign in"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px bg-theme-border flex-1" />
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">Or</span>
            <div className="h-px bg-theme-border flex-1" />
          </div>

          <GoogleSignInButton
            onClick={() => {
              setError("");
              startGoogleAuth();
            }}
            disabled={loading}
            loading={loading}
          />
        </div>

        <p className="text-center mt-6 font-mono text-[11px] text-theme-dim leading-relaxed">
          By using FlipMail you agree to the{" "}
          <a
            href="https://flipmail.ai/terms-of-service/"
            className="text-theme-muted underline underline-offset-2 hover:text-theme-accent"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>
          ,{" "}
          <a
            href="https://flipmail.ai/privacy-policy/"
            className="text-theme-muted underline underline-offset-2 hover:text-theme-accent"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          , and the{" "}
          <a
            href="https://flipmail.ai/help/google-access/"
            className="text-theme-muted underline underline-offset-2 hover:text-theme-accent"
            target="_blank"
            rel="noopener noreferrer"
          >
            Gmail data policy
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default Login;
